import React, { useContext, useState, useEffect } from "react";
import { Modal, Button, TextInput, Label } from "flowbite-react";
import Swal from "sweetalert2";
import axios from "axios";
import DataTable from "react-data-table-component";
import Select from "react-select";
import GlobalContext from "../connections/GlobalContext";
import engineGif from "../image/engine.gif";
import Head_data from "../components/Head_data";

export const Advance = ({ theme }) => {
    const { globalValue } = useContext(GlobalContext);

    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("add");

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);

    // AUTO-DUPLICATE APPEND STATES
    const [tempOldAmount, setTempOldAmount] = useState(0);
    const [autoAppendMode, setAutoAppendMode] = useState(false);

    const [editId, setEditId] = useState(null);
    const [hasExistingAdvance, setHasExistingAdvance] = useState(false);
    const [existingAdvanceData, setExistingAdvanceData] = useState(null);

    // LOG VIEW STATES
    const [logOpen, setLogOpen] = useState(false);
    const [logData, setLogData] = useState([]);
    const [logCustomer, setLogCustomer] = useState("");

    // EDIT RECORDS STATES
    const [editRecords, setEditRecords] = useState([]);
    const [editRowIndex, setEditRowIndex] = useState(null);
    const [editRowData, setEditRowData] = useState({ amount: "", date: "" });
    const [rowSaving, setRowSaving] = useState(false);

    /* ---------------- FETCH DATA ---------------- */

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(`${globalValue}/customers`, {
                params: {
                    data: sessionStorage.getItem("branch"),
                    position: "",
                },
                headers: { "x-access-token": sessionStorage.getItem("token") },
            });
            setCustomers(response.data || []);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    const fetchCustomerAdvance = async () => {
        try {
            const res = await axios.get(`${globalValue}/advance_display`, {
                params: { data: sessionStorage.getItem("branch") },
            });
            setAdvances(res.data || []);
        } catch (error) {
            console.error("Error fetching advances:", error);
        }
    };

    useEffect(() => {
        fetchCustomers();
        fetchCustomerAdvance();
    }, []);

    // AUTO-DUPLICATE DETECTION — only in ADD mode
    useEffect(() => {
        if (selectedCustomer && advances.length > 0 && mode === "add") {
            const customerData = customers.find((c) => c[1] === selectedCustomer.value);
            const vehicleNo = customerData ? customerData[4] : null;

            const existing = advances.find(
                (a) =>
                    a.vehicle_no === vehicleNo &&
                    a.customer_id === selectedCustomer.value
            );

            if (existing) {
                setHasExistingAdvance(true);
                setExistingAdvanceData(existing);
                setEditId(existing.id);
                setTempOldAmount(parseFloat(existing.amount) || 0);
                setAmount("0");
                setDate(new Date().toISOString().split("T")[0]);
                setAutoAppendMode(true);
            } else {
                setHasExistingAdvance(false);
                setExistingAdvanceData(null);
                setEditId(null);
                setTempOldAmount(0);
                setAutoAppendMode(false);
                setAmount("");
            }
        }
    }, [selectedCustomer, advances, customers, mode]);

    const fetchAdvanceLog = async (advance_id, customerName, vehicle_no) => {
        try {
            const response = await axios.get(`${globalValue}/advance_log_display`, {
                params: { advance_id },
            });
            setLogData(response.data.data || []);
            setLogCustomer(`${customerName} (${vehicle_no})`);
            setLogOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const filteredData = advances.filter(
        (row) =>
            row.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.vehicle_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.mobile_no?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const customerOptions = customers.map((c) => ({
        value: c[1],
        label: `${c[2]} (${c[4]})`,
    }));

    /* ---------------- OPEN HANDLERS ---------------- */

    const handleOpenAdd = () => {
        setMode("add");
        setSelectedCustomer(null);
        setAmount("");
        setDate(new Date().toISOString().split("T")[0]);
        setEditId(null);
        setHasExistingAdvance(false);
        setExistingAdvanceData(null);
        setTempOldAmount(0);
        setAutoAppendMode(false);
        setEditRecords([]);
        setEditRowIndex(null);
        setOpen(true);
    };

    const handleEditCustomer = (row) => {
        setMode("edit");
        setEditId(row.id);
        setSelectedCustomer({
            value: row.customer_id,
            label: `${row.customer_name} (${row.vehicle_no})`,
        });
        setAmount(row.amount);
        setHasExistingAdvance(false);
        setAutoAppendMode(false);

        let parsedRecords = [];
        try {
            parsedRecords =
                typeof row.records === "string"
                    ? JSON.parse(row.records)
                    : row.records || [];
        } catch {
            parsedRecords = [];
        }
        setEditRecords(parsedRecords);
        setEditRowIndex(null);
        setOpen(true);
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete?",
            text: "Are you sure?",
            icon: "warning",
            showCancelButton: true,
        });
        if (!confirm.isConfirmed) return;
        try {
            await axios.post(`${globalValue}/advance_delete`, { id });
            Swal.fire("Deleted!");
            fetchCustomerAdvance();
        } catch {
            Swal.fire("Error!", "Failed to delete.", "error");
        }
    };

    /* ---------------- ROW EDIT HELPERS ---------------- */

    const handleRecordEditStart = (index) => {
        setEditRowIndex(index);
        setEditRowData({
            amount: editRecords[index].amount,
            date: editRecords[index].date,
        });
    };

    const handleRecordEditCancel = () => setEditRowIndex(null);

    const handleRecordEditSave = async (index) => {
        if (!editRowData.amount || !editRowData.date) {
            Swal.fire({ icon: "warning", title: "Amount and Date are required." });
            return;
        }

        const rec = editRecords[index];

        try {
            setRowSaving(true);
            await axios.post(`${globalValue}/advance_records_edit`, {
                id: editId,
                sno: rec.sno ?? index + 1,
                amount: editRowData.amount,
                date: editRowData.date,
            });

            const updatedRecords = editRecords.map((r, i) =>
                i === index
                    ? { ...r, amount: editRowData.amount, date: editRowData.date }
                    : r
            );
            setEditRecords(updatedRecords);
            setEditRowIndex(null);

            Swal.fire({
                icon: "success",
                title: "Record Updated!",
                timer: 1500,
                showConfirmButton: false,
            });

            fetchCustomerAdvance();
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Something went wrong!",
            });
        } finally {
            setRowSaving(false);
        }
    };

    const handleRecordDelete = async (index) => {
        const rec = editRecords[index];

        const confirm = await Swal.fire({
            title: "Delete this record?",
            text: `S.No ${rec.sno ?? index + 1} — ₹${parseFloat(rec.amount || 0).toLocaleString()} on ${rec.date}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete it",
        });
        if (!confirm.isConfirmed) return;

        try {
            setRowSaving(true);
            await axios.post(`${globalValue}/advance_records_delete`, {
                id: editId,
                sno: rec.sno ?? index + 1,
                amount: rec.amount,
                date: rec.date,
            });

            const updatedRecords = editRecords
                .filter((_, i) => i !== index)
                .map((r, i) => ({ ...r, sno: i + 1 }));
            setEditRecords(updatedRecords);

            Swal.fire({
                icon: "success",
                title: "Record Deleted!",
                timer: 1500,
                showConfirmButton: false,
            });

            fetchCustomerAdvance();
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Something went wrong!",
            });
        } finally {
            setRowSaving(false);
        }
    };

    /* ---------------- ADD MODE SUBMIT ---------------- */

    const handleSubmit = async () => {
        if (!selectedCustomer || !amount || !date) {
            Swal.fire({ icon: "warning", title: "Missing Fields", text: "All fields are required!" });
            return;
        }

        const enteredAmount = parseFloat(amount) || 0;
        let payload;
        let apiEndpoint;
        let successMessage;

        if (autoAppendMode) {
            const finalAmount = tempOldAmount + enteredAmount;
            payload = {
                customer_id: selectedCustomer.value,
                amount: finalAmount.toString(),
                date,
                id: editId,
            };
            apiEndpoint = `${globalValue}/advance_edit`;
            successMessage = `Advance Updated! New Total: ₹${finalAmount.toLocaleString()}`;
        } else {
            payload = {
                customer_id: selectedCustomer.value,
                amount: enteredAmount.toString(),
                date,
                branch: sessionStorage.getItem("branch"),
            };
            apiEndpoint = `${globalValue}/advance_add`;
            successMessage = "Advance Added!";
        }

        try {
            setLoading(true);
            await axios.post(apiEndpoint, payload);

            Swal.fire({
                icon: "success",
                title: successMessage,
                timer: 2000,
                showConfirmButton: false,
            });
            setOpen(false);
            fetchCustomerAdvance();
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Something went wrong!",
            });
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- HELPER: can this record be safely deleted? ---------------- */
    // Hide delete if (totalAmount - thisRecordAmount) < 0
    const canDeleteRecord = (rec) => {
        const totalAmount = parseFloat(amount || 0);
        const recordAmount = parseFloat(rec.amount || 0);
        return (totalAmount - recordAmount) >= 0;
    };

    /* ---------------- TABLE STYLES ---------------- */

    const customStyles = {
        table: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                width: "100%",
                border:
                    theme === "dark"
                        ? "solid 1px rgb(63, 62, 62)"
                        : "solid 1px rgb(226, 232, 240)",
            },
        },
        headCells: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
                color: theme === "dark" ? "white" : "black",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                color: theme === "dark" ? "white" : "black",
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                border:
                    theme === "dark"
                        ? "solid 1px rgb(63, 62, 62)"
                        : "solid 1px rgb(255, 255, 255)",
            },
        },
        pagination: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                color: theme === "dark" ? "white" : "black",
                borderTop:
                    theme === "dark"
                        ? "solid 1px white"
                        : "solid 1px rgb(226, 232, 240)",
            },
            pageButtonsStyle: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                color: theme === "dark" ? "white" : "black",
                fill: theme === "dark" ? "white" : "black",
                "&:hover": {
                    backgroundColor:
                        theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
                },
            },
        },
    };

    const columns = [
        { name: "S.No", selector: (row) => row.sno, sortable: false, width: "80px" },
        {
            name: "Customer",
            selector: (row) => `${row.customer_name} (${row.vehicle_no})`,
            sortable: true,
        },
        { name: "Mobile", selector: (row) => row.mobile_no },
        {
            name: "Balance Amount",
            selector: (row) => `₹${parseFloat(row.amount).toLocaleString()}`,
        },
        {
            name: "Date",
            selector: (row) => {
                const d = new Date(row.date);
                return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
            },
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex gap-3">
                    <button
                        onClick={() => handleEditCustomer(row)}
                        className="p-2 text-white bg-blue-700 hover:bg-blue-800 rounded-md shadow-xl"
                        title="Edit"
                    >
                        <i className="fa fa-edit"></i>
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="p-2 text-white bg-yellow-500 hover:bg-yellow-700 rounded-md shadow-xl"
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                    <button
                        onClick={() => fetchAdvanceLog(row.id, row.customer_name, row.vehicle_no)}
                        className="p-2 text-white bg-green-600 hover:bg-green-700 rounded-md shadow-xl"
                        title="View Log"
                    >
                        <i className="fa fa-eye"></i>
                    </button>
                </div>
            ),
        },
    ];

    /* ---------------- RENDER ---------------- */

    return (
        <div
            className={`md:ml-[250px] mt-16 h-[92vh] flex justify-center items-center ${
                theme === "dark" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"
            }`}
        >
            {loading && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
                    <img src={engineGif} alt="Loading..." className="w-24 h-24" />
                </div>
            )}

            <div className="w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl bg-white overflow-auto">
                <div className="w-full flex justify-between text-gray-600">
                    <Head_data
                        data="Manage Customer Advance"
                        size="3xl"
                        color={theme === "dark" ? "text-gray-200" : "text-gray-800"}
                    />
                    <button
                        onClick={handleOpenAdd}
                        className="px-4 py-2 mt-4 text-white bg-blue-600 rounded hover:bg-blue-700 md:mt-2"
                    >
                        Add Advance
                    </button>
                </div>

                <input
                    type="text"
                    placeholder="Search customer, vehicle, mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full md:w-1/3 px-4 my-2 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none"
                />

                <DataTable
                    columns={columns}
                    data={filteredData.map((row, idx) => ({ ...row, sno: idx + 1 }))}
                    highlightOnHover
                    pagination
                    customStyles={customStyles}
                />
            </div>

            {/* ═══════════════════════════════════════════
                ADD / EDIT MODAL
            ═══════════════════════════════════════════ */}
            <Modal
                show={open}
                onClose={() => setOpen(false)}
                size={mode === "edit" ? "3xl" : "md"}
            >
                <Modal.Header>
                    {autoAppendMode
                        ? "Update Advance (Append Amount)"
                        : mode === "add"
                        ? "Add Advance"
                        : "Edit Advance Records"}
                </Modal.Header>

                <Modal.Body>

                    {/* ── ADD MODE ───────────────────────────────────── */}
                    {mode === "add" && (
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <Label value="Customer" />
                                <Select
                                    options={customerOptions}
                                    value={selectedCustomer}
                                    onChange={setSelectedCustomer}
                                    placeholder="Select customer"
                                />
                                {autoAppendMode && (
                                    <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                        <p className="text-yellow-800 text-sm">
                                            <strong>Old Amount:</strong> ₹{tempOldAmount.toLocaleString()}
                                            &nbsp;|&nbsp;
                                            <strong>New Total:</strong> ₹
                                            {(tempOldAmount + (parseFloat(amount) || 0)).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label value={autoAppendMode ? "New Amount to Add" : "Amount"} />
                                <TextInput
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder={
                                        autoAppendMode
                                            ? "Enter amount to ADD to existing"
                                            : "Enter amount"
                                    }
                                />
                            </div>

                            <div>
                                <Label value="Date" />
                                <TextInput
                                    type="date"
                                    value={date}
                                    disabled={sessionStorage.getItem("position") !== "superadmin"}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* ── EDIT MODE: records table ────────────────────── */}
                    {mode === "edit" && (
                        <div>
                            {/* Customer info banner */}
                            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                                <span className="text-blue-800 font-medium text-sm">
                                    {selectedCustomer?.label}
                                </span>
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded font-semibold">
                                    Total: ₹{parseFloat(amount || 0).toLocaleString()}
                                </span>
                            </div>

                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="border-b px-3 py-2 text-left w-14">S.No</th>
                                            <th className="border-b px-3 py-2 text-left">Date</th>
                                            <th className="border-b px-3 py-2 text-left">Amount (₹)</th>
                                            {/* Edit column hidden — only Delete shown */}
                                            <th className="border-b px-3 py-2 text-center w-28">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editRecords.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="text-center py-6 text-gray-400">
                                                    No records found
                                                </td>
                                            </tr>
                                        )}
                                        {editRecords.map((rec, index) => {
                                            // FIX: hide Delete if deleting this record would make total go negative
                                            const deletable = canDeleteRecord(rec);

                                            return (
                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50"
                                                >
                                                    {/* S.No */}
                                                    <td className="border-b px-3 py-2 text-gray-500">
                                                        {rec.sno ?? index + 1}
                                                    </td>

                                                    {/* Date — read-only, no inline edit */}
                                                    <td className="border-b px-3 py-2">
                                                        {rec.date}
                                                    </td>

                                                    {/* Amount — read-only, no inline edit */}
                                                    <td className="border-b px-3 py-2">
                                                        ₹{parseFloat(rec.amount || 0).toLocaleString()}
                                                    </td>

                                                    {/* Actions — Edit hidden, Delete shown only when safe */}
                                                    <td className="border-b px-3 py-2 text-center">
                                                        {deletable ? (
                                                            <button
                                                                onClick={() => handleRecordDelete(index)}
                                                                disabled={rowSaving}
                                                                className="px-2 py-1 text-xs text-white bg-red-500 hover:bg-red-600 rounded disabled:opacity-60"
                                                            >
                                                                <i className="fa fa-trash mr-1"></i>Delete
                                                            </button>
                                                        ) : (
                                                            // Show a subtle lock indicator so user understands why no button
                                                            <span
                                                                className="text-xs text-gray-400 italic"
                                                                title="Cannot delete — would make balance negative"
                                                            >
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    {mode === "add" && (
                        <Button
                            color={autoAppendMode ? "warning" : "blue"}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading
                                ? "Processing..."
                                : autoAppendMode
                                ? `Update (Total: ₹${(tempOldAmount + (parseFloat(amount) || 0)).toLocaleString()})`
                                : "Add Advance"}
                        </Button>
                    )}
                    <Button
                        color="gray"
                        onClick={() => setOpen(false)}
                        disabled={loading || rowSaving}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ═══════════════════════════════════════════
                LOG VIEW MODAL
            ═══════════════════════════════════════════ */}
            <Modal show={logOpen} onClose={() => setLogOpen(false)} size="6xl">
                <Modal.Header>Advance Log – {logCustomer}</Modal.Header>
                <Modal.Body>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-300">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="border px-3 py-2">Date</th>
                                    <th className="border px-3 py-2">Amount</th>
                                    <th className="border px-3 py-2">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logData.length > 0 ? (
                                    logData.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-100">
                                            <td className="border px-3 py-2 text-center">
                                                {new Date(item.date).toISOString().split("T")[0]}
                                            </td>
                                            <td className="border px-3 py-2 text-center">{item.amount}</td>
                                            <td className="border px-3 py-2 text-center">{item.description}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center p-3 text-gray-500">
                                            No logs found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button color="gray" onClick={() => setLogOpen(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};